import { Tabs, router, useFocusEffect } from "expo-router";
import React, { useCallback, useEffect, useState } from "react";
import {
  Platform,
  StatusBar,
  View,
  TouchableOpacity,
  Image,
} from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Colors } from "@/constants/Colors";
import Tabbar from "@/components/ui/Tabbar";
import useUserStore from "@/store/user";
import useAppStore from "@/store/app";
import Icons from "@/assets/icons";
import Constants from "expo-constants";
import useWindow from "@/hooks/useWindow";
import {
  registerForPushNotificationsAsync,
  useNotification,
} from "@/hooks/useNotification";
import NotificationServices from "@/services/notificationServices";
import { useSubscriptionRecovery } from "@/hooks/sharedHook/useSubscriptionRecovery";
import { APP_BASE_URL } from "@/constants/Configs";
import { getGlobalSessionId } from "@/services";
import { isWeb } from "@/utils/platform";
import { ChatPopup } from "@/components/chat";

export default function TabLayout() {
  const { getUserProfile, isFreePlan, userProfile } = useUserStore();
  const { hasSession, isInitialized } = useAppStore();
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);
  useNotification();
  useSubscriptionRecovery(); // Auto-recover orphaned IAP subscriptions

  // Auth guard: redirect unauthenticated users
  useEffect(() => {
    if (isInitialized && !hasSession) {
      if (isWeb) {
        window.location.href = APP_BASE_URL;
      } else {
        router.replace("/welcome");
      }
    }
  }, [isInitialized, hasSession]);

  // Paywall guard: redirect web users without a plan to paywall
  useEffect(() => {
    if (isWeb && userProfile && isFreePlan) {
      const sessionId = getGlobalSessionId();
      if (sessionId) {
        window.location.href = `${APP_BASE_URL}/pay?sessionId=${sessionId}&fromWebApp=true`;
      }
    }
  }, [userProfile, isFreePlan]);

  useFocusEffect(
    useCallback(() => {
      getUserProfile();
    }, [])
  );

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    const token = await registerForPushNotificationsAsync();
    if (token) {
      await NotificationServices.initDevice({ token });
    }
  };

  const { isDesktop } = useWindow();

  // Don't render until auth state is confirmed
  if (!isInitialized || !hasSession) {
    return null;
  }

  return (
    <View style={isDesktop ? styles.containerDesktop : styles.container}>
      <StatusBar
        barStyle="light-content"
        translucent
        backgroundColor="transparent"
      />
      <View style={styles.content}>
        <Tabs
          screenOptions={{
            tabBarPosition: isDesktop ? "left" : "bottom",
            headerShown: false,
            sceneStyle: {
              backgroundColor: Colors.background,
            },
            animation: isDesktop ? "fade" : undefined,
          }}
          tabBar={(props) => <Tabbar {...props} />}
        >
          <Tabs.Screen
            name="feeds"
            options={{
              title: "My Claims",
              tabBarIcon: ({ focused }) =>
                focused ? (
                  <Icons.HandGiveActive width={28} height={28} />
                ) : (
                  <Icons.HandGive width={28} height={28} />
                ),
            }}
          />
          <Tabs.Screen
            name="my-money"
            options={{
              title: "My Money",
              tabBarIcon: ({ focused }) =>
                focused ? (
                  <Icons.MoneyCircleActive width={28} height={28} />
                ) : (
                  <Icons.MoneyCircle width={28} height={28} />
                ),
            }}
          />
          <Tabs.Screen
            name="account"
            options={{
              title: "Account",
              tabBarIcon: ({ focused }) =>
                focused ? (
                  <Icons.ProfileUser width={28} height={28} />
                ) : (
                  <Icons.ProfileUserInactive width={28} height={28} />
                ),
            }}
          />
        </Tabs>
      </View>

      {/* Desktop Chat Button - Bottom Right */}
      {isDesktop && (
        <TouchableOpacity
          style={styles.chatButtonDesktop}
          onPress={() => setIsChatPopupOpen(true)}
          activeOpacity={0.8}
        >
          <Image
            source={require("@/assets/icons/claim-chat.png")}
            style={styles.chatButtonImageDesktop}
            resizeMode="cover"
          />
        </TouchableOpacity>
      )}

      {/* Chat Popup for Desktop */}
      {isDesktop && (
        <ChatPopup
          isVisible={isChatPopupOpen}
          onClose={() => setIsChatPopupOpen(false)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  containerDesktop: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    backgroundColor: Colors.background,
    paddingTop: Platform.select({
      web: 0,
      default: Constants.statusBarHeight,
    }),
  },
  chatButtonDesktop: {
    position: "absolute",
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 8,
  },
  chatButtonImageDesktop: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
});
